// ─────────────────────────────────────────────────────────────────────────────
// Single source of truth for the Doorly Program Agreement terms shown on
// the frontend. Used by:
//   • AdminBorrowerProfile.jsx — DoorlyDesk SmartEstimate "view" modal
// (Terms.js names this file only in a comment.)
//
// The PDF generator (backend/utils/generateSmartEstimatePdf.js) reads the
// same text from backend/constants/doorlyProgramTerms.js — the two files
// must stay in lockstep when copy changes (same pattern as
// frontend/src/constants/statusColors.js mirroring backend/constants/stages.js).
// backend/test/doorlyProgramTermsMirror.test.js fails if the agreement text or
// any export present in both files differs.
// ─────────────────────────────────────────────────────────────────────────────

// ⚠️ The agreement footer below ends with "visit help.godoorly.com". That link
// must STAY. The help centre is moving from Intercom to a native build at the
// SAME subdomain — the destination changes underneath it, the URL does not.
// The comment lives here rather than beside the line because the URL sits inside
// this template literal and anything added there would print in the signed
// agreement. See backend/knowledge/docs/help/MIGRATION.md.
// (2026-10-21) RESALE_CAP_PCT is gone: Realtor Services v2.5 §3.2 no longer states a resale cap, and the program has
// no resale adjustment for new files.

export const DOORLY_PROGRAM_TERMS = `DOORLY PROGRAM AGREEMENT

1. Engagement
Doorly agrees to provide services in accordance with its home financing and acquisition program (the "Program"). Doorly retains full control over how such services are delivered, while taking into consideration the Client's preferences and objectives throughout the process.

2. Funds to Close
The Client is responsible for providing all required funds to close, including but not limited to:


Down payment
Closing costs
Appraisal package costs
Origination fees
Any other applicable transaction-related costs

Closing funds are delivered in two separate wire transfers:

Down Payment — Due within 3 business days of your contract effective date. This confirms your commitment to the transaction and enables Doorly to proceed with the cash purchase.
Remaining Cash to Close — Due 3 business days before your closing date. This covers closing costs, prepaids, and escrow deposits as shown on your Closing Disclosure.

A Loan Estimate will be provided once an offer on a property is accepted, and a final Closing Disclosure will be delivered at least three (3) days prior to closing.

3. Standards of Performance
Doorly represents that it has the experience, knowledge, and expertise necessary to perform the services described in the Program.

Doorly will perform all services in a professional, diligent, and commercially reasonable manner, consistent with applicable laws and industry standards.

4. Third-Party Fees
The Client acknowledges that certain fees may be paid to or received from third parties involved in the transaction, including but not limited to:
- Real estate commissions
- Administrative or processing fees
- Other transaction-related costs

These fees are separate from amounts paid directly by the Client and do not impact the Client's obligations under this agreement.

5. Proprietary Rights
a. Doorly Intellectual Property
In connection with providing services, Doorly may disclose certain proprietary information, including processes, methodologies, systems, and operational frameworks (collectively, "Intellectual Property").

The Client agrees:
- Not to disclose or distribute such information without prior written consent
- To use such information solely for purposes related to the Program

6. Severability
If any provision of this agreement is determined to be invalid, illegal, or unenforceable, the remaining provisions shall remain in full force and effect.

7. Homeowners Insurance
A homeowners insurance policy is required for all properties purchased through the Program.
- The first year's premium will be paid at or before closing
- Ongoing premiums will be paid through escrow and managed by the designated loan servicer
- The Client is responsible for maintaining continuous coverage

8. Closing Timeline & Housing Responsibility
Doorly works to facilitate a smooth and timely closing process; however, delays may occur due to factors outside of Doorly's control, including:
- Title or lender processing delays
- Appraisal scheduling or valuation issues
- Findings from your appraisal package
- Other unforeseen circumstances

The Client is responsible for arranging any temporary housing prior to closing. Doorly is not responsible for housing or related costs during this period.

9. Transaction Contingency
Doorly's model involves acquiring the property prior to resale to the Client.

If Doorly's initial acquisition of the property does not occur for any reason:
- The subsequent transaction between Doorly and the Client will be void
- All related obligations will terminate
- Any funds paid by the Client may be credited toward a future transaction, subject to approval and program eligibility

10. Purchase Price
The price you pay Doorly for the Property is the same as the contract price in Doorly's purchase agreement with the seller. Doorly does not add a markup. In that purchase, Doorly may negotiate and receive seller-paid concessions, and the real estate brokerage representing Doorly may receive compensation from the seller. Those amounts are not passed on to you, and they do not increase your price.

11. Property Showings
Each Client receives three (3) complimentary property showings as part of the Program.

After the complimentary showings have been used, each additional showing is subject to a $60 showing fee, payable at the time of scheduling. Showing fees are non-refundable and are not credited toward closing costs.

Doorly coordinates showings through licensed showing agents. The Client will be contacted directly by the assigned agent to arrange property access.

12. Purchase Initiation Deposit
When you ask Doorly to start buying a home for you, you pay a Purchase Initiation Deposit in the amount shown at payment. Doorly holds it in a separate account. It is a deposit toward your purchase, not a fee, and not a charge for credit.

- If the seller does not accept Doorly's offer, or the home does not qualify, you may apply your deposit to another home or request a full refund.
- If you decide not to continue before you sign a purchase agreement with Doorly, you may request a full refund.
- Once you sign a purchase agreement with Doorly, your deposit is governed by that agreement. It is credited in full toward your purchase at closing, and may be forfeited only if you fail to complete the purchase as that agreement provides.

──────────────────────────────────────────
Go Doorly, LLC | NMLS #2764749 | Equal Housing Lender
Questions? Contact Doorly or visit help.godoorly.com`;

// ── Mirrors of the backend's other two exports (backend/constants/doorlyProgramTerms.js) ──
// No frontend screen reads these today (the option rate is served by the backend); they are mirrored so the two
// files export the same names with the same values. The backend file carries the reasoning for each entry.
// ⚠️ Option 5's retiredOn is recorded at server boot (backend utils/resaleRemovalDate), so it cannot be typed here:
// it is null in this mirror, and the mirror test accepts null only where the backend value is computed.
export const RESALE_ADJUSTMENT_OPTIONS = {
  selectedBy: 'borrower',
  selectedAt: 'approval',
  shownAs: 'the SmartEstimate card on the borrower dashboard',
  mutuallyExclusive: false,
  options: [
    { name: 'SmartEstimate', resaleAdjustmentPct: 0, programFeePct: 2, programFeeBasis: 'loan' },
  ],
  retired: [
    { option: 1, name: 'Option 1', resaleAdjustmentPct: 5,  programFeePct: 2, retiredOn: '2026-09-21' },
    { option: 2, name: 'Option 2', resaleAdjustmentPct: 10, programFeePct: 0, retiredOn: '2026-09-21' },
    { option: 3, name: 'SmartEstimate (3%)', resaleAdjustmentPct: 10, programFeePct: 3, retiredOn: '2026-09-22' },
    { option: 4, name: 'SmartEstimate (2% of price)', resaleAdjustmentPct: 10, programFeePct: 2, retiredOn: '2026-09-23' },
    { option: 5, name: 'SmartEstimate (10% resale)', resaleAdjustmentPct: 10, programFeePct: 2, programFeeBasis: 'loan',
      retiredOn: null },
  ],
};

export const PROGRAM_AGREEMENT_VERSION = 'purchase-price-1';   // §10 "Purchase Price" — a text version, not a date
